import {
	Alert,
	AlertDescription,
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Combobox,
	type ComboboxOption,
	Input,
	Label,
	Switch,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	Textarea,
} from "@ancatag/at-design";
import {
	AlertCircle,
	ArrowLeft,
	CheckCircle2,
	Info,
	Loader2,
	Radio,
	RefreshCw,
	Save,
	Settings,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useLocation, useRoute } from "wouter";
import {
	type ModelConfig,
	modelConfigsService,
	type UpdateModelConfigData,
} from "../../services/models";
import type { ProviderModel } from "../../services/route-configs";

export function EditModel() {
	const [, setLocation] = useLocation();
	const [match, params] = useRoute("/models/manage/:id/edit");
	const modelId = params?.id;

	const [model, setModel] = useState<ModelConfig | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [testing, setTesting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Model selection state
	const [availableModels, setAvailableModels] = useState<ProviderModel[]>([]);
	const [loadingModels, setLoadingModels] = useState(false);
	const [modelSearchValue, setModelSearchValue] = useState<string>("");

	// Form state
	const [formData, setFormData] = useState<UpdateModelConfigData>({
		name: "",
		description: "",
		modelIdentifier: "",
		modelDisplayName: "",
		credentials: {},
		isActive: true,
	});

	useEffect(() => {
		if (!match || !modelId) {
			setError("Invalid route");
			setLoading(false);
			return;
		}

		const fetchModel = async () => {
			try {
				setLoading(true);
				setError(null);
				const response = await modelConfigsService.get(modelId);
				// apiFetch may unwrap the data, so handle both cases
				const data = response.data || response;
				if (!data || !data.id) {
					throw new Error("Invalid model data received");
				}
				setModel(data);
				setFormData({
					name: data.name || "",
					description: data.description || "",
					modelIdentifier: data.modelIdentifier || "",
					modelDisplayName: data.modelDisplayName || data.modelIdentifier || "",
					credentials: {}, // Credentials are not returned for security, user must re-enter if updating
					isActive: data.isActive ?? true,
				});
			} catch (err) {
				const errorMessage =
					err instanceof Error ? err.message : "Failed to load model";
				setError(errorMessage);
				toast.error(`Failed to load model: ${errorMessage}`);
			} finally {
				setLoading(false);
			}
		};

		fetchModel();
	}, [match, modelId]);

	// Fetch available models from provider
	useEffect(() => {
		if (!model || !modelId) return;

		const fetchModels = async () => {
			try {
				setLoadingModels(true);
				const models = await modelConfigsService.listProviderModels(modelId);
				setAvailableModels(models);
			} catch (err) {
				console.warn("Failed to fetch provider models:", err);
				// Don't show error toast - this is optional functionality
				setAvailableModels([]);
			} finally {
				setLoadingModels(false);
			}
		};

		fetchModels();
	}, [model, modelId]);

	// Convert ProviderModel[] to ComboboxOption[]
	const modelOptions: ComboboxOption[] = useMemo(() => {
		return availableModels.map((m) => ({
			value: m.id, // Use model ID (e.g., "chat:latest", "llama2:7b")
			label: m.name || m.id, // Display name or ID
			description:
				m.description ||
				(m.contextWindow
					? `${m.contextWindow.toLocaleString()} context window`
					: undefined),
		}));
	}, [availableModels]);

	// Handle model selection change
	const handleModelChange = (value: string) => {
		setModelSearchValue(value);

		// Auto-populate name based on selected model
		const selectedModel = availableModels.find((m) => m.id === value);
		if (selectedModel) {
			// Found in list - use model name
			const currentName = formData.name || "";
			const shouldAutoPopulate =
				!currentName ||
				currentName === model?.modelIdentifier ||
				currentName === model?.name;

			setFormData((prev) => ({
				...prev,
				modelIdentifier: value,
				name: shouldAutoPopulate ? selectedModel.name || value : prev.name,
				modelDisplayName: selectedModel.name || value,
			}));
		} else if (value) {
			// Custom model identifier - use it as the name if name is empty or matches old value
			const currentName = formData.name || "";
			const shouldAutoPopulate =
				!currentName ||
				currentName === model?.modelIdentifier ||
				currentName === model?.name;

			setFormData((prev) => ({
				...prev,
				modelIdentifier: value,
				name: shouldAutoPopulate ? value : prev.name,
				modelDisplayName: value,
			}));
		} else {
			// Empty value
			setFormData((prev) => ({
				...prev,
				modelIdentifier: value,
			}));
		}
	};

	// Refresh available models
	const handleRefreshModels = async () => {
		if (!modelId) return;
		try {
			setLoadingModels(true);
			const models = await modelConfigsService.listProviderModels(modelId);
			setAvailableModels(models);
			toast.success(
				`Found ${models.length} available model${models.length !== 1 ? "s" : ""}`,
			);
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to refresh models";
			toast.error(errorMessage);
		} finally {
			setLoadingModels(false);
		}
	};

	const handleSave = async () => {
		if (
			!modelId ||
			!formData.name?.trim() ||
			!formData.modelIdentifier?.trim()
		) {
			toast.error("Name and model identifier are required");
			return;
		}

		setSaving(true);
		try {
			// Only include credentials if at least one field is provided
			const credentials =
				formData.credentials &&
				Object.keys(formData.credentials).some(
					(key) => (formData.credentials as any)[key],
				)
					? formData.credentials
					: undefined;

			const updated = await modelConfigsService.update(modelId, {
				...formData,
				description: formData.description || undefined,
				modelDisplayName: formData.modelDisplayName || undefined,
				credentials,
			});
			toast.success("Model connection updated successfully");
			// apiFetch may unwrap the data, so handle both cases
			const updatedData = updated.data || updated;
			if (updatedData) {
				setModel(updatedData);
			}
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to update model",
			);
		} finally {
			setSaving(false);
		}
	};

	const handleTest = async () => {
		if (!modelId) return;
		setTesting(true);
		try {
			const result = await modelConfigsService.testConnection(modelId);
			if (result.success) {
				toast.success(`Connection test successful! (${result.responseTime}ms)`);
				// Refresh model to get updated status
				const refreshed = await modelConfigsService.get(modelId);
				// apiFetch may unwrap the data, so handle both cases
				const refreshedData = refreshed.data || refreshed;
				if (refreshedData) {
					setModel(refreshedData);
				}
			} else {
				toast.error(
					`Connection test failed: ${result.error || "Unknown error"}`,
				);
			}
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Connection test failed",
			);
		} finally {
			setTesting(false);
		}
	};

	// Show error if route doesn't match
	if (!match) {
		return (
			<div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8">
				<div className="flex items-center gap-3 mb-6">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => setLocation("/models/manage")}
						className="h-8 w-8"
					>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<h1 className="text-3xl font-bold">Edit Model Connection</h1>
				</div>
				<Alert variant="destructive">
					<AlertCircle className="h-4 w-4" />
					<AlertDescription>
						Invalid route. Please navigate from the models page.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8">
				<div className="space-y-4">
					<div className="h-10 w-64 bg-muted animate-pulse rounded" />
					<Card>
						<CardContent className="p-6">
							<div className="space-y-4">
								<div className="h-4 w-full bg-muted animate-pulse rounded" />
								<div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	if (error || !model) {
		return (
			<div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8">
				<div className="flex items-center gap-3 mb-6">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => setLocation("/models/manage")}
						className="h-8 w-8"
					>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<h1 className="text-3xl font-bold">Edit Model Connection</h1>
				</div>
				<Alert variant="destructive">
					<AlertCircle className="h-4 w-4" />
					<AlertDescription>
						{error || "Model connection not found"}
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	const status = model?.connectionStatus || "pending";
	const hasError = status === "error";

	return (
		<div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8">
			{/* Header with actions */}
			<div className="flex items-start justify-between mb-6">
				<div className="flex-1">
					<div className="flex items-center gap-3 mb-2">
						<Button
							variant="ghost"
							size="icon"
							onClick={() => setLocation("/models/manage")}
							className="h-8 w-8"
						>
							<ArrowLeft className="h-4 w-4" />
						</Button>
						<div>
							<h1 className="text-3xl font-bold">Edit Model Connection</h1>
							<p className="text-muted-foreground mt-1">{model?.name || ""}</p>
						</div>
					</div>
				</div>
				<div className="flex items-center gap-2 ml-4">
					<Button variant="outline" onClick={handleTest} disabled={testing}>
						<Radio
							className={`h-4 w-4 mr-2 ${testing ? "animate-pulse" : ""}`}
						/>
						{testing ? "Testing..." : "Test Connection"}
					</Button>
					<Button onClick={handleSave} disabled={saving}>
						<Save className="h-4 w-4 mr-2" />
						{saving ? "Saving..." : "Save Changes"}
					</Button>
				</div>
			</div>

			{/* Error/Status Alerts */}
			{hasError && model?.connectionError && (
				<Alert variant="destructive" className="mb-6">
					<AlertCircle className="h-4 w-4" />
					<AlertDescription>
						<div className="font-medium mb-1">Connection Error</div>
						<div className="text-sm">{model.connectionError}</div>
						<div className="text-xs mt-2 opacity-80">
							Update credentials or check your provider settings, then test the
							connection again.
						</div>
					</AlertDescription>
				</Alert>
			)}

			{status === "connected" && (
				<Alert className="mb-6 border-green-500/20 bg-green-500/5">
					<CheckCircle2 className="h-4 w-4 text-green-600" />
					<AlertDescription className="text-green-400">
						Connection is healthy
					</AlertDescription>
				</Alert>
			)}

			<Tabs defaultValue="overview" className="w-full">
				<TabsList
					className={`grid w-full mb-6 ${
						model?.isHosted ? "grid-cols-1" : "grid-cols-2"
					}`}
				>
					<TabsTrigger value="overview" className="flex items-center gap-2">
						<Info className="h-4 w-4" />
						Overview
					</TabsTrigger>
					{!model?.isHosted && (
						<TabsTrigger value="connection" className="flex items-center gap-2">
							<Settings className="h-4 w-4" />
							Connection
						</TabsTrigger>
					)}
				</TabsList>

				<TabsContent value="overview" className="space-y-6">
					<div className="grid gap-6 md:grid-cols-2">
						{/* Basic Info */}
						<Card>
							<CardHeader>
								<CardTitle>Basic Information</CardTitle>
								<CardDescription>
									Name and description for this model connection
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div>
									<Label htmlFor="name">Name *</Label>
									<Input
										id="name"
										value={formData.name || ""}
										onChange={(e) =>
											setFormData({ ...formData, name: e.target.value })
										}
										placeholder="My Chat Model"
									/>
								</div>
								<div>
									<Label htmlFor="description">Description</Label>
									<Textarea
										id="description"
										value={formData.description || ""}
										onChange={(e) =>
											setFormData({ ...formData, description: e.target.value })
										}
										placeholder="Optional description..."
										rows={3}
									/>
								</div>
								<div className="flex items-center justify-between pt-2">
									<div>
										<Label>Active</Label>
										<p className="text-xs text-muted-foreground">
											Enable this model connection
										</p>
									</div>
									<Switch
										checked={formData.isActive ?? true}
										onCheckedChange={(v) =>
											setFormData({ ...formData, isActive: v })
										}
									/>
								</div>
							</CardContent>
						</Card>

						{/* Provider & Model */}
						<Card>
							<CardHeader>
								<CardTitle>Provider & Model</CardTitle>
								<CardDescription>
									Provider and model information
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div>
									<Label>Provider</Label>
									<div className="mt-1">
										<Badge variant="outline" className="capitalize">
											{model?.providerType || "N/A"}
										</Badge>
									</div>
								</div>
								<div>
									<div className="flex items-center justify-between mb-2">
										<Label htmlFor="modelIdentifier">Model Identifier *</Label>
										{model && !model.isHosted && (
											<Button
												type="button"
												variant="ghost"
												size="sm"
												onClick={handleRefreshModels}
												disabled={loadingModels}
												className="h-7 text-xs"
											>
												<RefreshCw
													className={`h-3 w-3 mr-1 ${loadingModels ? "animate-spin" : ""}`}
												/>
												Refresh
											</Button>
										)}
									</div>
									{modelOptions.length > 0 ? (
										<Combobox
											options={modelOptions}
											value={formData.modelIdentifier || ""}
											onValueChange={handleModelChange}
											placeholder="Select or type model identifier..."
											searchPlaceholder="Search models..."
											emptyText={`No models found matching "${modelSearchValue}". Type a custom model identifier.`}
											loading={loadingModels}
											allowCustom={true}
										/>
									) : loadingModels ? (
										<div className="flex items-center gap-2">
											<Input
												id="modelIdentifier"
												value={formData.modelIdentifier || ""}
												onChange={(e) => {
													const value = e.target.value;
													setFormData({ ...formData, modelIdentifier: value });
												}}
												placeholder="Loading models..."
												disabled
											/>
											<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
										</div>
									) : (
										<Input
											id="modelIdentifier"
											value={formData.modelIdentifier || ""}
											onChange={(e) => {
												const value = e.target.value;
												setFormData({ ...formData, modelIdentifier: value });
												// Auto-populate name if empty
												if (
													!formData.name ||
													formData.name === model?.modelIdentifier ||
													formData.name === model?.name
												) {
													setFormData((prev) => ({
														...prev,
														modelIdentifier: value,
														name: value,
														modelDisplayName: value,
													}));
												}
											}}
											placeholder="chat, llama2, gpt-4o"
										/>
									)}
									<p className="text-xs text-muted-foreground mt-1.5">
										{modelOptions.length > 0
											? `Select from ${modelOptions.length} available model${modelOptions.length !== 1 ? "s" : ""} or type a custom identifier`
											: loadingModels
												? "Fetching available models from provider..."
												: 'The model identifier used by the provider (e.g., "chat", "llama2", "gpt-4o"). Click Refresh to load available models.'}
									</p>
								</div>
								<div>
									<Label htmlFor="modelDisplayName">Model Display Name</Label>
									<Input
										id="modelDisplayName"
										value={formData.modelDisplayName || ""}
										onChange={(e) =>
											setFormData({
												...formData,
												modelDisplayName: e.target.value,
											})
										}
										placeholder="Chat Model, Llama 2 7B, GPT-4o"
									/>
									<p className="text-xs text-muted-foreground mt-1.5">
										Optional friendly name for display purposes
									</p>
								</div>
								<div>
									<Label>Generation Type</Label>
									<div className="mt-1">
										<Badge variant="outline" className="capitalize">
											{model?.generationType || "N/A"}
										</Badge>
									</div>
								</div>
								<div>
									<Label>Mode</Label>
									<div className="mt-1">
										<Badge variant={model?.isHosted ? "default" : "secondary"}>
											{model?.isHosted ? "Hosted" : "BYOP"}
										</Badge>
									</div>
								</div>
								<div>
									<Label>Connection Status</Label>
									<div className="mt-1">
										<Badge
											variant="outline"
											className={
												status === "connected"
													? "border-green-500 text-green-600"
													: status === "error"
														? "border-red-500 text-red-600"
														: "border-yellow-500 text-yellow-600"
											}
										>
											{status === "connected"
												? "Connected"
												: status === "error"
													? "Error"
													: "Pending"}
										</Badge>
									</div>
								</div>
							</CardContent>
						</Card>
					</div>
				</TabsContent>

				{!model?.isHosted && (
					<TabsContent value="connection" className="space-y-6">
						{/* Credentials */}
						<Card>
							<CardHeader>
								<CardTitle>Credentials</CardTitle>
								<CardDescription>Provider connection settings</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{model?.hasCredentials && (
									<Alert>
										<AlertCircle className="h-4 w-4" />
										<AlertDescription>
											Credentials are stored securely. Leave fields empty to
											keep existing credentials, or enter new values to update
											them.
										</AlertDescription>
									</Alert>
								)}
								<div>
									<Label htmlFor="baseUrl">
										Base URL {model?.hasCredentials ? "" : "*"}
									</Label>
									<Input
										id="baseUrl"
										type="url"
										value={(formData.credentials as any)?.baseUrl || ""}
										onChange={(e) =>
											setFormData({
												...formData,
												credentials: {
													...((formData.credentials as any) || {}),
													baseUrl: e.target.value,
												},
											})
										}
										placeholder={
											model?.hasCredentials
												? "Leave empty to keep existing"
												: "http://localhost:11434"
										}
									/>
								</div>
								{model?.providerType !== "ollama" && (
									<>
										<div>
											<Label htmlFor="apiKey">API Key</Label>
											<Input
												id="apiKey"
												type="password"
												value={(formData.credentials as any)?.apiKey || ""}
												onChange={(e) =>
													setFormData({
														...formData,
														credentials: {
															...((formData.credentials as any) || {}),
															apiKey: e.target.value,
														},
													})
												}
												placeholder={
													model?.hasCredentials
														? "Leave empty to keep existing"
														: "sk-..."
												}
											/>
										</div>
										<div>
											<Label htmlFor="orgId">Organization ID</Label>
											<Input
												id="orgId"
												value={(formData.credentials as any)?.orgId || ""}
												onChange={(e) =>
													setFormData({
														...formData,
														credentials: {
															...((formData.credentials as any) || {}),
															orgId: e.target.value,
														},
													})
												}
												placeholder={
													model?.hasCredentials
														? "Leave empty to keep existing"
														: "org-..."
												}
											/>
										</div>
									</>
								)}
							</CardContent>
						</Card>
					</TabsContent>
				)}
			</Tabs>
		</div>
	);
}
